import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  BONUS_SCHEDULE_FIELDS,
  bonusTypeDisplayLabel,
} from "helpers/bonusScheduleTypeData";
import { handleUpdateFields } from "store/liveOps/bonuses/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import FilterTabs from "components/FilterTabs";
import CompactMultiSelectDropdown, {
  CompactSelectItem,
} from "components/dropdowns/CompactMultiSelectDropdown/CompactMultiSelectDropdown";
import { getSegmentsListService } from "services/segments/userSegmentService";
import { ImageUpload } from "components/imageUpload";
import Tooltip from "../../../../components/Tooltip";

interface BonusMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly?: boolean;
}

const BonusesMainTab: React.FC<BonusMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payload = useAppSelector((store) => store.bonuses.payload);
  const payoutCurrencies = useAppSelector(
    (store) => store.settings?.payoutCurrencies ?? [],
  );
  const typesList = useAppSelector((store) => store.bonuses.typesList);
  const isListTypesFetched = useAppSelector(
    (store) => store.bonuses.isListTypesFetched,
  );
  const statusList = useAppSelector((store) => store.bonuses.statusList);
  const isListStatusFetched = useAppSelector(
    (store) => store.bonuses.isListStatusFetched,
  );
  const wagerList = useAppSelector((store) => store.bonuses.wagerList);
  const isListWagerFetched = useAppSelector(
    (store) => store.bonuses.isListWagerFetched,
  );
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [optionsStatus, setOptionsStatus] = useState<
    { value: string; label: string }[]
  >([]);
  const [optionsWager, setOptionsWager] = useState<
    { value: string; label: string }[]
  >([]);
  const [segmentOptions, setSegmentOptions] = useState<CompactSelectItem[]>([]);
  const categoryOptions = [
    { value: "all", label: "All" },
    { value: "casino", label: "Casino" },
    { value: "sport", label: "Sport" },
  ];
  const wagerBalancePriorityOptions = [
    { value: "any", label: t("anyBalance") },
    { value: "real", label: t("realBalance") },
    { value: "bonus", label: t("bonusBalance") },
  ];
  const bankrollOptions = [
    ...(payload?.isRealWagering
      ? [{ value: "real", label: t("realBalance") }]
      : []),
    ...(payload?.isBonusWagering
      ? [{ value: "bonus", label: t("bonusBankrollBalance") }]
      : []),
  ];

  useEffect(() => {
    if (isListTypesFetched && typesList.length > 0) {
      const array = typesList.map((el: any) => {
        return {
          value: el.id,
          label: bonusTypeDisplayLabel(el.title, t),
        };
      });
      setOptions(array);
    }
    if (isListStatusFetched && statusList.length > 0) {
      const array = statusList.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptionsStatus(array);
    }
    if (isListWagerFetched && wagerList.length > 0) {
      const array = wagerList.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptionsWager(array);
    }
  }, [
    isListTypesFetched,
    isListStatusFetched,
    isListWagerFetched,
    typesList,
    statusList,
    wagerList,
    t,
  ]);

  const handleData = (
    key: string,
    value: string | boolean | number | number[] | string[],
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  useEffect(() => {
    const loadSegmentOptions = async () => {
      try {
        const res = await getSegmentsListService({
          limit: 1000,
          offset: 0,
          order: { title: "asc" },
        });

        setSegmentOptions(
          res.map((segment) => ({
            value: segment.id,
            label: `${segment.title} (${segment.type}) · ${segment.countOfUsers} users`,
          })),
        );
      } catch (error) {
        console.error("Failed to load segment options", error);
      }
    };

    loadSegmentOptions();
  }, []);

  return (
    <div className="bonus-form-content bonus-main-form-content bonus-main-fields">
      <div className="bonus-form-box">
        <div className="bonus-form-box--item">
          <InnerInput
            value={payload?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="bonus-title"
            onChange={(e) => {
              handleData("title", e);
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
          />
        </div>
        <div className="bonus-form-box--item">
          <InnerInput
            maxCodePoints={2500}
            value={payload?.description ?? ""}
            placeholder={t("enterText")}
            label={t("description")}
            type="textarea"
            id="bonus-description"
            onChange={(e) => {
              handleData("description", e);
              clearFieldError("description");
            }}
            hasError={Boolean(fieldErrors?.["description"])}
          />
        </div>

        <div className="bonus-form-box--item bonus-form-box--row bonus-type-row">
          <DefaultDropdown
            value={payload?.typeId}
            placeholder={t("bonusType")}
            label={t("type")}
            tooltipText={t("bonusTriggerTooltip")}
            tooltipPosition="top-left"
            options={options}
            disabled={
              isReadOnly || options.length === 0 || !isListTypesFetched
            }
            isShort
            onSelect={(e) => {
              handleData("typeId", e);
              clearFieldError("typeId");
              BONUS_SCHEDULE_FIELDS.forEach((field) => clearFieldError(field));
            }}
            hasError={Boolean(fieldErrors?.["typeId"])}
          />
          <div className="bonus-form-switcher bonus-guest-switcher">
            <div className="title">
              {t("showForGuests")} {" "}
              <Tooltip text={t("showForGuestsTooltip")}>
                <span className="info-icon">ℹ</span>
              </Tooltip>
            </div>
            <SwitchBtn
              disabled={isReadOnly}
              isActive={payload.canGuestShow}
              handleClick={() => {
                handleData("canGuestShow", !payload.canGuestShow);
              }}
            />
          </div>
        </div>
        <div className="bonus-form-box--item bonus-form-box--row">
          <DefaultDropdown
            disabled={isReadOnly}
            value={payload?.category}
            placeholder="Category"
            label="Category"
            options={categoryOptions}
            isShort
            isTop
            onSelect={(e) => {
              handleData("category", e);
            }}
          />
          <DefaultDropdown
            value={payload?.statusId}
            placeholder={t("bonusStatus")}
            label={t("status")}
            tooltipText={t("currentStateOfTheBonus")}
            tooltipPosition="top-left"
            options={optionsStatus}
            disabled={
              isReadOnly || optionsStatus.length === 0 || !isListStatusFetched
            }
            isShort
            isTop
            onSelect={(e) => {
              handleData("statusId", e);
              clearFieldError("statusId");
            }}
            hasError={Boolean(fieldErrors?.["statusId"])}
          />
        </div>

        {payload?.typeId?.toString() === "3" && (
          <div className="bonus-form-box--item">
            <FilterTabs
              value={payload?.typeData}
              isInner
              onSelect={(e) => handleData("typeData", e)}
              options={[
                {
                  value: "email",
                  label: t("email"),
                },
                {
                  value: "kyc",
                  label: "KYC",
                },
                {
                  value: "phone",
                  label: t("phone"),
                },
              ]}
            />
          </div>
        )}
        <div className="bonus-form-box--item bonus-form-box--row">
          <InnerInput
            value={payload?.payoutX?.toString() ?? ""}
            placeholder={t("enterNumber")}
            label={t("maxWinMultiplier")}
            tooltipText={t("maxWithdrawalLimit")}
            tooltipPosition="top-left"
            type="number"
            id="bonus-payoutX"
            onChange={(e) => {
              handleData("payoutX", e);
              clearFieldError("payoutX");
            }}
            hasError={Boolean(fieldErrors?.["payoutX"])}
          />
          <InnerInput
            value={payload?.wagerX?.toString() ?? ""}
            placeholder={t("enterNumber")}
            label={t("wagerX")}
            tooltipText={t("wagerRequirementMultiplier")}
            tooltipPosition="top-left"
            type="number"
            id="bonus-description"
            onChange={(e) => {
              handleData("wagerX", e);
              clearFieldError("wagerX");
            }}
            hasError={Boolean(fieldErrors?.["wagerX"])}
          />
        </div>
        <div className="bonus-form-box--item bonus-form-box--wager">
          <div className="bonus-compact-multi-select">
            <div className="bonus-compact-multi-select__label">
              {t("wagerGames")}
            </div>
            <CompactMultiSelectDropdown
              disabled={isReadOnly}
              label={t("wagerGames")}
              items={optionsWager}
              selectedValues={payload?.wagerGamesListIds ?? []}
              onChange={(values) => {
                handleData("wagerGamesListIds", values.map(Number));
                clearFieldError("wagerGamesListIds");
              }}
              isTop
            />
          </div>
        </div>
        <div className="bonus-form-box--row bonus-form-segments bonus-form-bottom-row">
          <div className="bonus-compact-multi-select">
            <div className="bonus-compact-multi-select__label">
              {t("includedSegments")}
            </div>
            <CompactMultiSelectDropdown
              disabled={isReadOnly}
              label={t("includedSegments")}
              items={segmentOptions}
              selectedValues={payload?.segmentIds ?? []}
              onChange={(values) => {
                handleData("segmentIds", values.map(Number));
                clearFieldError("segmentIds");
              }}
              isTop
            />
          </div>
          <div className="bonus-compact-multi-select">
            <div className="bonus-compact-multi-select__label">
              {t("excludedSegments")}
            </div>
            <CompactMultiSelectDropdown
              disabled={isReadOnly}
              label={t("excludedSegments")}
              items={segmentOptions}
              selectedValues={payload?.excludedSegmentIds ?? []}
              onChange={(values) => {
                handleData("excludedSegmentIds", values.map(Number));
                clearFieldError("excludedSegmentIds");
              }}
              isTop
            />
          </div>
        </div>
      </div>

      <div className="bonus-form-box">
        <div className="bonus-form-img bonus-main-assets">
          <div className="bonus-images">
            <div className="bonus-image-item desktop">
                <ImageUpload
                  label="Desktop"
                  labelClassName="bonus-main-form-label"
                  image={payload?.image}
                allowMp4
                size="large"
                onAddImage={(file) => {
                  handleData("image", "", file);
                  clearFieldError("file");
                }}
                isActive={true}
                hasError={!!fieldErrors?.["image"]}
              />
            </div>

            <div className="bonus-images-secondary">
              <div className="bonus-image-item mobile">
                <ImageUpload
                  label="Mobile"
                  labelClassName="bonus-main-form-label"
                  image={payload?.mobileImage}
                  allowMp4
                  size="large"
                  type="landscape"
                  aspect="21:9"
                  onAddImage={(file) => {
                    handleData("mobileImage", "", file);
                    clearFieldError("mobileImage");
                  }}
                  isActive={true}
                  hasError={!!fieldErrors?.["mobileImage"]}
                />
              </div>

              <div className="bonus-image-item bonus-portrait">
                <ImageUpload
                  label="Portrait"
                  labelClassName="bonus-main-form-label"
                  image={payload?.portraitImage}
                  allowMp4
                  size="large"
                  type="portrait"
                  aspect="9:16"
                  onAddImage={(file) => {
                    handleData("portraitImage", "", file);
                    clearFieldError("portraitImage");
                  }}
                  isActive={true}
                  hasError={!!fieldErrors?.["portraitImage"]}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bonus-form-wager-balance bonus-form-wager-switches">
          <div className="bonus-form-switcher">
            <div className="title">
              {t("balanceWagered")}{" "}
              <Tooltip text={t("definesWhichBalance")}>
                <span className="info-icon">ℹ</span>
              </Tooltip>
            </div>
            <SwitchBtn
              activeTxt={t("bonus")}
              isActive={!!payload?.isBonusWagering}
              handleClick={() => {
                handleData(
                  "isBonusWagering",
                  payload?.isBonusWagering ? !payload?.isBonusWagering : true,
                );
                clearFieldError("isBonusWagering");
              }}
              hasError={Boolean(fieldErrors?.["isBonusWagering"])}
            />
            <SwitchBtn
              activeTxt={t("real")}
              isActive={!!payload?.isRealWagering}
              handleClick={() => {
                handleData(
                  "isRealWagering",
                  payload?.isRealWagering ? !payload?.isRealWagering : true,
                );
                clearFieldError("isRealWagering");
              }}
              hasError={Boolean(fieldErrors?.["isRealWagering"])}
            />
          </div>
        </div>
        <div className="bonus-form-wager-balance bonus-form-priority-label">
          <DefaultDropdown
            disabled={isReadOnly}
            value={payload?.wagerBalancePriority}
            placeholder={t("anyBalance")}
            label={t("balancePriority")}
            options={wagerBalancePriorityOptions}
            isShort
            isTop
            onSelect={(value) => {
              handleData("wagerBalancePriority", value);
              clearFieldError("wagerBalancePriority");
            }}
            hasError={Boolean(fieldErrors?.["wagerBalancePriority"])}
          />
          <InnerInput
            disabled={isReadOnly}
            value={payload?.label ?? ""}
            placeholder=""
            label="Label UI"
            type="text"
            id="bonus-label"
            maxCodePoints={255}
            onChange={(value) => {
              handleData("label", value);
              clearFieldError("label");
            }}
            hasError={Boolean(fieldErrors?.["label"])}
          />
        </div>

        <div className="bonus-form-box--item bonus-form-bankroll-mode">
          <FilterTabs
            disabled={isReadOnly}
            label={t("bankrollMode")}
            value={payload?.bankrollMode}
            options={[
              { value: "separately", label: t("separately") },
              { value: "together", label: t("together") },
            ]}
            onSelect={(value) => {
              handleData("bankrollMode", value);
              clearFieldError("bankrollMode");
            }}
            hasError={Boolean(fieldErrors?.["bankrollMode"])}
          />
        </div>

        <div className="bonus-form-wager-balance bonus-form-bankroll-options bonus-form-bottom-row">
          <DefaultDropdown
            disabled={
              isReadOnly ||
              payload?.bankrollMode !== "together" ||
              bankrollOptions.length === 0
            }
            value={payload?.priorityBankroll ?? undefined}
            placeholder={t("bankrollPriority")}
            label={t("bankrollPriority")}
            tooltipText={t("bankrollPriorityTooltip")}
            tooltipPosition="top-left"
            options={bankrollOptions}
            isShort
            isTop
            onSelect={(value) => {
              handleData("priorityBankroll", value);
              clearFieldError("priorityBankroll");
            }}
            hasError={Boolean(fieldErrors?.["priorityBankroll"])}
          />
          <DefaultDropdown
            disabled={
              isReadOnly ||
              payload?.bankrollMode !== "together" ||
              bankrollOptions.length === 0
            }
            value={payload?.payoutDestination ?? undefined}
            placeholder={t("payoutDestination")}
            label={t("payoutDestination")}
            tooltipText={t("payoutDestinationTooltip")}
            tooltipPosition="top-left"
            options={bankrollOptions}
            isShort
            isTop
            onSelect={(value) => {
              handleData("payoutDestination", value);
              clearFieldError("payoutDestination");
            }}
            hasError={Boolean(fieldErrors?.["payoutDestination"])}
          />
          <DefaultDropdown
            disabled={isReadOnly}
            value={payload?.payoutCurrency || ""}
            placeholder={t("payoutCurrency")}
            label={t("payoutCurrency")}
            tooltipText={t("payoutCurrencyTooltip")}
            tooltipPosition="top-left"
            options={[
              { value: "", label: t("payoutCurrencyNone") },
              ...payoutCurrencies.map((currency: string) => ({
                value: currency,
                label: currency,
              })),
            ]}
            isShort
            isTop
            onSelect={(value) => {
              dispatch(
                handleUpdateFields({
                  payoutCurrency:
                    typeof value === "string" && value ? value : null,
                }),
              );
              clearFieldError("payoutCurrency");
            }}
            hasError={Boolean(fieldErrors?.["payoutCurrency"])}
          />
        </div>
      </div>
    </div>
  );
};

export default BonusesMainTab;
